import React from "react";
import { ArrowUpRight } from "lucide-react";

const slides = ["/hero-v1.jpg", "/hero-v2.jpg", "/hero-v3.jpg", "/hero-v4.jpg"];

const floatingStats = [
  { k: "80%", l: "less manual work" },
  { k: "∞", l: "AI that evolves with you" },
  { k: "3×", l: "faster responses" },
];

export const CurrentBackdrop = () =>
  slides.map((src, i) => (
    <div key={i} className={`hero-slide hero-slide-${i + 1}`}>
      <img src={src} alt="" loading={i === 0 ? "eager" : "lazy"} />
    </div>
  ));

export const LiveBackdrop = () => (
  <>
    <img data-testid="hero-still" className="heroA-video" src="/heroB-3.jpg" alt="" />
    {["/heroB-1.jpg", "/heroB-2.jpg", "/heroB-3.jpg"].map((src, i) => (
      <div key={src} className={`heroLive-slide heroLive-slide-${i + 1}`}>
        <img src={src} alt="" loading="lazy" />
      </div>
    ))}
    <span className="heroB-line heroLive-line" />
  </>
);

export default function Hero({ backdrop = <LiveBackdrop /> }) {
  return (
    <section
      id="top"
      className="relative overflow-hidden min-h-[92vh] bg-[var(--night-deep)]"
    >
      {/* ─── Full-bleed cinematic backdrop ─── */}
      <div aria-hidden="true" className="absolute inset-0">
        {backdrop}

        {/* Dark readability veil — heavier on the left where text sits */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(100deg, rgba(10,16,32,0.4) 0%, rgba(10,16,32,0.2) 40%, rgba(10,16,32,0.0) 70%, rgba(10,16,32,0.15) 100%)",
          }}
        />
        {/* Bottom gradient for stat chips contrast */}
        <div
          className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, transparent, rgba(10,16,32,0.38))",
          }}
        />
        {/* Warm wash to tie into brand */}
        <div
          className="absolute inset-0 pointer-events-none mix-blend-soft-light"
          style={{
            background:
              "radial-gradient(ellipse 50% 60% at 70% 55%, rgba(232,206,149,0.35), transparent 70%)",
          }}
        />
        </div>

      {/* ─── Content column — left aligned over the imagery ─── */}
      <div className="relative z-10 px-5 sm:px-10 lg:px-16 pt-28 sm:pt-28 pb-44 sm:pb-36">
        <div
          data-testid="hero-text-panel"
          className="max-w-[620px] rounded-[28px] border border-white/70 bg-[rgba(242,245,251,0.94)] backdrop-blur-xl shadow-[0_40px_80px_-30px_rgba(5,10,25,0.6)] p-7 sm:p-10"
        >
          <h1
            data-testid="hero-headline"
            className="reveal font-serif font-medium text-[var(--ink)] text-[46px] leading-[0.98] sm:text-[64px] sm:leading-[0.95] md:text-[74px] tracking-[-0.035em]"
            style={{ animationDelay: "60ms" }}
          >
            The AI systems
            <br />
            <span className="italic font-italic-serif font-light text-[var(--ink-soft)]">
              that run your business
            </span>
            <br />
            <span className="relative inline-block">
              — built{" "}
              <span className="relative inline-block">
                <span className="relative z-10" style={{ color: "#A8823A" }}>
                  with
                </span>
                <span
                  className="absolute left-0 right-0 bottom-1 sm:bottom-2 h-2 sm:h-2.5 rounded-sm -z-0"
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(232,206,149,0.0) 0%, rgba(232,206,149,0.75) 50%, rgba(232,206,149,0.0) 100%)",
                  }}
                />
              </span>{" "}
              you.
            </span>
          </h1>

          <p
            className="reveal mt-7 text-[var(--ink-soft)] text-[16.5px] sm:text-[18px] leading-[1.55] max-w-[560px]"
            style={{ animationDelay: "140ms" }}
          >
            We handle the AI so you don&apos;t have to. We partner with you so
            you can focus on your business, and{" "}
            <span className="text-[var(--ink)] font-medium">we evolve your AI</span> as your
            business grows.
          </p>

          <div
            className="reveal mt-8 flex flex-wrap items-center gap-3"
            style={{ animationDelay: "220ms" }}
          >
            <a
              data-testid="hero-cta-primary"
              href="#book"
              className="btn-cobalt rounded-full text-[14px] font-medium pl-5 pr-4 py-3.5 inline-flex items-center gap-2"
            >
              Book free call
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <a
              data-testid="hero-cta-secondary"
              href="#agents"
              className="rounded-full text-[14px] font-medium px-5 py-3.5 inline-flex items-center gap-2 border border-[var(--ink)]/15 bg-white/60 text-[var(--ink)] hover:bg-white transition-colors"
            >
              What we build
              <span style={{ color: "#A8823A" }}>↓</span>
            </a>
          </div>

        </div>
      </div>

      {/* ─── Floating stat chips, bottom-right ─── */}
      <div className="absolute left-5 sm:left-8 right-5 sm:right-8 bottom-6 sm:bottom-8 z-10 flex items-end justify-between gap-4">
        {/* bottom-left brass hairline */}
        <div className="hidden sm:flex items-center gap-3 text-white/55 max-w-[360px]">
          <span className="h-px flex-1 bg-gradient-to-r from-[var(--camel)]/80 via-white/20 to-transparent" />
          <span className="font-mono text-[9.5px] tracking-[0.3em] uppercase whitespace-nowrap">
            Est. 2024 · St. Petersburg · FL
          </span>
        </div>

        {/* chips */}
        <div className="flex items-end gap-3 ml-auto">
          {floatingStats.map((s, i) => (
            <div
              key={i}
              data-testid={`hero-stat-${i}`}
              className={`reveal rounded-2xl px-4 py-3 min-w-[120px] sm:min-w-[140px] ${i === 2 ? "hidden sm:block" : ""} border border-white/15 bg-white/[0.06] backdrop-blur-md shadow-[0_20px_40px_-20px_rgba(0,0,0,0.5)]`}
              style={{ animationDelay: `${340 + i * 110}ms` }}
            >
              <div className="flex items-end gap-1.5">
                <div className="font-serif text-[26px] leading-none text-white">
                  {s.k}
                </div>
                <span
                  className="w-1 h-1 rounded-full mb-2"
                  style={{ background: "#E8CE95" }}
                />
              </div>
              <div className="font-mono text-[9.5px] tracking-[0.18em] uppercase text-white/60 mt-2 leading-tight">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* brass corner accents */}
      <div className="absolute top-16 left-5 sm:left-8 w-8 h-8 pointer-events-none z-10">
        <span
          className="absolute top-0 left-0 w-full h-px"
          style={{ background: "#E8CE95", opacity: 0.6 }}
        />
        <span
          className="absolute top-0 left-0 w-px h-full"
          style={{ background: "#E8CE95", opacity: 0.6 }}
        />
      </div>
      <div className="absolute bottom-24 right-5 sm:right-8 w-8 h-8 pointer-events-none z-10">
        <span
          className="absolute bottom-0 right-0 w-full h-px"
          style={{ background: "#E8CE95", opacity: 0.6 }}
        />
        <span
          className="absolute bottom-0 right-0 w-px h-full"
          style={{ background: "#E8CE95", opacity: 0.6 }}
        />
      </div>
    </section>
  );
}
